import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const targetCategoryLabel = {
  scholarship: "Beasiswa",
  university: "Universitas luar negeri (S1 self-funded)",
};

const studyLevelLabel = {
  S1: "S1",
  S2: "S2",
  S3: "S3",
};

const englishUsageLabel = {
  daily: "Setiap hari",
  weekly: "Beberapa kali seminggu",
  rarely: "Jarang",
  never: "Hampir tidak pernah",
};

const learningBackgroundLabel = {
  formal: "Kursus formal",
  informal: "Kursus informal / bimbel",
  self_study: "Belajar mandiri",
  none: "Belum pernah belajar intensif",
};

const durationLabel = {
  "0": "Belum pernah belajar",
  "1": "Kurang dari 1 bulan",
  "3": "1-3 bulan",
  "6": "4-6 bulan",
  "12": "7-12 bulan",
  "24": "Lebih dari 2 tahun",
};

const getBandLabel = (value) => {
  if (!value) {
    return "-";
  }

  return `${value} band`;
};

const getCategoryLabel = (value) => {
  if (value === "ready") return "Ready";
  if (value === "need_prep") return "Need Preparation";
  if (value === "need_intensive") return "Need Intensive Prep";
  return "Need Preparation";
};

const getGapLabel = (value) => {
  if (value === null || value === undefined) {
    return "-";
  }

  return `Gap ke target: ~${value} poin IELTS`;
};

const getPriorityInsight = (insights) => {
  if (!Array.isArray(insights) || insights.length === 0) {
    return "Insight belum tersedia";
  }

  return insights[0].text || "Insight belum tersedia";
};

const Step4 = ({
  currentStep,
  totalSteps,
  answers,
  selectedScholarship,
  reportData,
  isSubmitting,
  submitError,
  onSubmit,
  onBack,
}) => {
  const navigate = useNavigate();
  const [showResult, setShowResult] = useState(false);

  const summaryRows = useMemo(
    () => [
      {
        label: "Jalur Masuk",
        value: answers.entryPath === "from_placement" ? "Path B (dari placement test)" : "Path A (skor IELTS manual)",
      },
      { label: "Jenis target", value: targetCategoryLabel[answers.targetType] || "-" },
      { label: "Target", value: answers.targetName || selectedScholarship?.name || "-" },
      { label: "Jenjang", value: studyLevelLabel[answers.degreeLevel] || "-" },
      { label: "Skor IELTS", value: getBandLabel(answers.ieltsOverall) },
      { label: "Min. target", value: selectedScholarship?.min_overall ? `${selectedScholarship.min_overall} band` : "-" },
      { label: "Penggunaan sehari-hari", value: englishUsageLabel[answers.englishUsageFrequency] || "-" },
      { label: "Background belajar", value: learningBackgroundLabel[answers.learningBackground] || "-" },
      { label: "Durasi belajar", value: durationLabel[answers.learningDurationMonths] || "-" },
    ],
    [answers, selectedScholarship]
  );

  const report = reportData?.report;

  const handleShowResult = async () => {
    setShowResult(true);
    try {
      await onSubmit();
    } catch (err) {
      // submit error already handled in parent state
    }
  };

  return (
    <>
      <p className="st-font-body st-text-body-muted text-[14px] sm:text-[15px]">
        Step {currentStep} of {totalSteps}
      </p>

      <h1 className="st-font-body mt-1.5 text-[28px] font-bold leading-[1.15] text-[#212121] sm:text-[34px]">
        <span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#BB230B] align-middle" />
        Konfirmasi sebelum submit
      </h1>
      <p className="st-font-body st-text-body-muted mt-1.5 text-[16px] sm:text-[18px]">
        Pastikan semua informasi berikut sudah benar sebelum kami menganalisis kesiapanmu.
      </p>

      <div className="mt-6 space-y-2.5">
        {summaryRows.map((row) => (
          <div key={row.label} className="grid grid-cols-[1fr_auto] gap-3 sm:gap-6">
            <p className="st-font-body text-[16px] font-bold leading-[1.3] text-[#252323] sm:text-[17px]">{row.label}</p>
            <p className="st-font-body text-right text-[16px] font-medium leading-[1.3] text-[#2E2A2A] sm:text-[17px]">{row.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-7 flex justify-between">
        <button type="button" className="st-focus-ring st-readiness-next" onClick={onBack}>
          ← Back
        </button>

        <button
          type="button"
          disabled={isSubmitting}
          className="st-focus-ring st-readiness-next st-readiness-next-auto"
          onClick={handleShowResult}
        >
          Lihat Hasil →
        </button>
      </div>

      {showResult && (
        <div className="mt-6 rounded-[14px] border border-[#6EA77B] bg-[#D6E6D8] px-5 py-4 sm:px-6 sm:py-5">
          <p className="st-font-body text-[26px] font-bold leading-none text-[#176B2B] sm:text-[28px]">READINESS RESULT</p>

          {isSubmitting && <p className="st-font-body mt-3 text-[15px] text-[#245D2E]">Sedang memproses hasil readiness...</p>}
          {submitError && !report && <p className="st-font-body mt-3 text-[15px] text-[#BB230B]">{submitError}</p>}

          <div className="mt-3 grid gap-4 sm:grid-cols-[auto_1fr] sm:items-start sm:gap-6">
            <p className="st-font-body text-[78px] font-bold leading-[0.9] text-[#176B2B] sm:text-[86px]">
              {report?.readiness_score ?? "-"}
            </p>

            <div>
              <div className="grid gap-2.5 sm:grid-cols-[auto_1fr] sm:items-center">
                <p className="st-font-body text-[34px] font-bold leading-[1] text-[#176B2B] sm:text-[38px]">Readiness Score</p>
                <p className="st-font-body rounded-[8px] border border-[#AECAB5] bg-[#E8F2E9] px-3 py-2 text-[16px] font-medium leading-[1.2] text-[#245D2E] sm:text-[17px]">
                  {getGapLabel(report?.gap_overall)}
                </p>
              </div>

              <div className="mt-2 grid gap-2.5 sm:grid-cols-[auto_1fr] sm:items-center">
                <p className="st-font-body inline-flex w-fit rounded-[8px] border border-[#6EA77B] bg-[#BFD9C2] px-3 py-1.5 text-[20px] font-bold leading-none text-[#176B2B] sm:text-[22px]">
                  {getCategoryLabel(report?.readiness_category)}
                </p>
                <p className="st-font-body rounded-[8px] border border-[#AECAB5] bg-[#E8F2E9] px-3 py-2 text-[16px] font-medium leading-[1.2] text-[#245D2E] sm:text-[17px]">
                  {getPriorityInsight(report?.insights)}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 text-center">
            <button
              type="button"
              disabled={!report?.whatsapp_url}
              onClick={() => window.open(report.whatsapp_url, "_blank", "noopener,noreferrer")}
              className="st-focus-ring st-font-body inline-flex items-center justify-center rounded-[10px] bg-[#1E7F34] px-5 py-2.5 text-[15px] font-bold text-white transition-colors hover:bg-[#176A2A] sm:text-[16px]"
            >
              Lihat langkah selanjutnya buat kamu ↗
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="st-focus-ring st-font-body mt-2 block w-full text-center text-[11px] text-[#6A7D6D] underline-offset-2 hover:underline"
            >
              Klik untuk kembali ke halaman utama ↗
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Step4;
